/**
 * GA4 Tracking Utilities for Languex Portal
 * Property: Languex GA4 – G-048Q503ERX
 */

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Initialize gtag function if not already available
 */
const initGtag = () => {
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer!.push(args);
    };
  }
};

/**
 * Track page_view event
 * Note: This is automatically handled by RouteTracker component
 * This function is provided for manual page view tracking if needed
 */
export const trackPageView = (pagePath?: string, pageTitle?: string) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath || window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: pageTitle || document.title,
    });
  }
};

/**
 * Set user properties for GA4
 */
export const setUserProperties = (userId: string | null, customerType: 'new' | 'returning') => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      customer_type: customerType,
    });
  }
};

/**
 * Track sign_up event
 */
export const trackSignUp = (userId: string, method: 'portal' | 'google' = 'portal') => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'sign_up', {
      method: method,
      user_id: userId,
      customer_type: 'new',
    });
  }
};

/**
 * Track login event
 */
export const trackLogin = (userId: string, method: 'password' | 'google' = 'password') => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'login', {
      user_id: userId,
      method: method,
      customer_type: 'returning',
    });
  }
};

/**
 * Track begin_checkout event
 */
export const trackBeginCheckout = (params: {
  userId: string | null;
  serviceType: 'certified' | 'standard';
  languagePair: string;
  checkoutSource?: string;
}) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      user_id: params.userId,
      service_type: params.serviceType,
      language_pair: params.languagePair,
      checkout_source: params.checkoutSource || 'portal',
    });
  }
};

/**
 * Track add_payment_info event
 */
export const trackAddPaymentInfo = (params: {
  userId: string | null;
  paymentProvider: 'stripe' | 'paypal';
  checkoutSource?: string;
}) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'add_payment_info', {
      user_id: params.userId,
      payment_provider: params.paymentProvider,
      checkout_source: params.checkoutSource || 'portal',
    });
  }
};

/**
 * Track purchase event
 */
export const trackPurchase = (params: {
  transactionId: string;
  value: number;
  currency?: string;
  userId: string | null;
  serviceType: 'certified' | 'standard';
  languagePair: string;
  paymentProvider: 'stripe' | 'paypal';
  isGuest: boolean;
  checkoutSource?: string;
}) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: params.transactionId,
      value: params.value,
      currency: params.currency || 'USD',
      user_id: params.userId,
      service_type: params.serviceType,
      language_pair: params.languagePair,
      payment_provider: params.paymentProvider,
      is_guest: params.isGuest,
      checkout_source: params.checkoutSource || 'portal',
    });
  }
};

/**
 * Track add_on_selected event
 */
export const trackAddOnSelected = (params: {
  userId: string | null;
  transactionId: string | null;
  addOn: 'proofreading' | 'desktop_publishing' | 'notarization' | 'hard_copy' | 'express_delivery';
  addOnValue: number;
  checkoutSource?: string;
}) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', 'add_on_selected', {
      user_id: params.userId,
      transaction_id: params.transactionId,
      add_on: params.addOn,
      add_on_value: params.addOnValue,
      checkout_source: params.checkoutSource || 'portal',
    });
  }
};

/**
 * Track first purchase and update customer_type to returning
 */
export const trackFirstPurchase = (userId: string) => {
  initGtag();
  
  // Mark user as having purchased before
  localStorage.setItem('languex_has_purchased_before', 'true');
  
  // Update customer_type to returning for future sessions
  if (window.gtag) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      customer_type: 'returning'
    });
  }
};

/**
 * Legacy tracking for backward compatibility - kept for existing custom events
 */
export const trackCustomEvent = (eventName: string, params: Record<string, any>) => {
  initGtag();
  
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
};
